import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.reignitespirit.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpeg`;

function normalizeCanonical(canonical) {
  if (!canonical) return "";

  if (
    canonical.startsWith("http://") ||
    canonical.startsWith("https://")
  ) {
    return canonical;
  }

  const normalizedPath = canonical.startsWith("/")
    ? canonical
    : `/${canonical}`;

  return `${SITE_URL}${normalizedPath}`;
}

function normalizeImage(image) {
  if (!image) return DEFAULT_IMAGE;

  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  const normalizedPath = image.startsWith("/")
    ? image
    : `/${image}`;

  return `${SITE_URL}${normalizedPath}`;
}

export default function SEO({
  title,
  description,
  canonical,
  path,
  image,
  type = "website",
  siteName = "ReigniteSpirit",
  author = "ReigniteSpirit",
  keywords = "",
  robots = "index, follow",
  publishedTime,
  modifiedTime,
  articleSection,
  schema = [],
  children,
}) {
  const canonicalValue = canonical || path || "";
  const finalCanonical = normalizeCanonical(canonicalValue);
  const finalImage = normalizeImage(image);

  const schemaItems = Array.isArray(schema)
    ? schema.filter(Boolean)
    : schema
    ? [schema]
    : [];

  const finalTitle = title
    ? title.includes("ReigniteSpirit")
      ? title
      : `${title} | ReigniteSpirit`
    : "ReigniteSpirit";

  return (
    <Helmet>
      {/* ================= PRIMARY SEO ================= */}

      <title>{finalTitle}</title>

      {description && (
        <meta
          name="description"
          content={description}
        />
      )}

      {keywords && (
        <meta
          name="keywords"
          content={keywords}
        />
      )}

      <meta
        name="author"
        content={author}
      />

      <meta
        name="robots"
        content={robots}
      />

      <meta
        name="googlebot"
        content={robots}
      />

      {finalCanonical && (
        <link
          rel="canonical"
          href={finalCanonical}
        />
      )}

      {/* ================= OPEN GRAPH ================= */}

      <meta
        property="og:type"
        content={type}
      />

      <meta
        property="og:site_name"
        content={siteName}
      />

      <meta
        property="og:title"
        content={finalTitle}
      />

      {description && (
        <meta
          property="og:description"
          content={description}
        />
      )}

      {finalCanonical && (
        <meta
          property="og:url"
          content={finalCanonical}
        />
      )}

      <meta
        property="og:image"
        content={finalImage}
      />

      <meta
        property="og:image:secure_url"
        content={finalImage}
      />

      <meta
        property="og:image:type"
        content="image/jpeg"
      />

      <meta
        property="og:image:width"
        content="1200"
      />

      <meta
        property="og:image:height"
        content="630"
      />

      <meta
        property="og:image:alt"
        content={finalTitle}
      />

      <meta
        property="og:locale"
        content="en_US"
      />

      {/* ================= ARTICLE SEO ================= */}

      {type === "article" && publishedTime && (
        <meta
          property="article:published_time"
          content={publishedTime}
        />
      )}

      {type === "article" && modifiedTime && (
        <meta
          property="article:modified_time"
          content={modifiedTime}
        />
      )}

      {type === "article" && author && (
        <meta
          property="article:author"
          content={author}
        />
      )}

      {type === "article" && articleSection && (
        <meta
          property="article:section"
          content={articleSection}
        />
      )}

      {/* ================= TWITTER ================= */}

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={finalTitle}
      />

      {description && (
        <meta
          name="twitter:description"
          content={description}
        />
      )}

      <meta
        name="twitter:image"
        content={finalImage}
      />

      <meta
        name="twitter:image:alt"
        content={finalTitle}
      />

      {/* ================= JSON-LD SCHEMAS ================= */}

      {schemaItems.map((schemaItem, index) => (
        <script
          key={`json-ld-${index}`}
          type="application/ld+json"
        >
          {JSON.stringify(schemaItem)}
        </script>
      ))}

      {/* Additional page-specific Helmet tags */}

      {children}
    </Helmet>
  );
}